import React, { useCallback, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '../../lib/utils';

export interface SegmentedNavItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  href?: string;
  matchPaths?: string[];
  onSelect?: () => void;
  disabled?: boolean;
}

interface SegmentedNavProps {
  items: SegmentedNavItem[];
  activeKey?: string;
  ariaLabel: string;
  className?: string;
  /**
   * Accessibility role. Default 'navigation' renders as a `<nav>` with
   * standard buttons / links. Use 'tablist' when this nav drives an
   * adjacent `<div role="tabpanel">` — that adds `role="tab"`,
   * `aria-selected`, `aria-controls`, and `id` on each button, plus
   * arrow-key / Home / End navigation between tabs. The
   * `tabPanelIdPrefix` is used to compute the `aria-controls` target
   * id; the consuming page should set the panel's `id` to
   * `${tabPanelIdPrefix}-${activeKey}` for the assistive-tech linkage.
   */
  role?: 'navigation' | 'tablist';
  /** Used when `role === 'tablist'` to compute `aria-controls`/`id`s. */
  tabPanelIdPrefix?: string;
}

const itemClasses =
  'inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[13px] font-semibold transition-[background-color,color,border-color,box-shadow]';

const activeClasses =
  'border-primary/15 bg-[var(--go-good-vibes-tint)] text-primary shadow-[var(--shadow-card)]';

const inactiveClasses =
  'border-border bg-card text-muted-foreground hover:border-primary/15 hover:text-foreground';

const disabledClasses = 'cursor-not-allowed opacity-50 pointer-events-none';

const SegmentedNav: React.FC<SegmentedNavProps> = ({
  items,
  activeKey,
  ariaLabel,
  className,
  role = 'navigation',
  tabPanelIdPrefix,
}) => {
  const location = useLocation();
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const isActive = (item: SegmentedNavItem) => {
    if (activeKey) {
      return activeKey === item.key;
    }

    if (item.href) {
      const matchPaths = item.matchPaths ?? [item.href];
      return matchPaths.some(
        (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    }

    return false;
  };

  // Arrow-key + Home/End handling for tablist mode. Per W3C APG tablist
  // pattern: Left/Right move focus and activate the next/previous tab.
  // Disabled tabs are skipped. Wraps around at the ends.
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, currentKey: string) => {
      if (role !== 'tablist') return;
      const enabledItems = items.filter((it) => !it.disabled);
      const idx = enabledItems.findIndex((it) => it.key === currentKey);
      if (idx === -1) return;
      let nextIdx: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
          nextIdx = (idx + 1) % enabledItems.length;
          break;
        case 'ArrowLeft':
          nextIdx = (idx - 1 + enabledItems.length) % enabledItems.length;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = enabledItems.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      const next = enabledItems[nextIdx];
      next.onSelect?.();
      // Move DOM focus to the newly-selected tab.
      tabRefs.current.get(next.key)?.focus();
    },
    [items, role],
  );

  const isTablist = role === 'tablist';

  return (
    <nav
      className={cn('flex flex-wrap gap-3', className)}
      aria-label={ariaLabel}
      role={isTablist ? 'tablist' : undefined}
    >
      {items.map((item) => {
        const active = isActive(item);
        const sharedClassName = cn(
          itemClasses,
          active ? activeClasses : inactiveClasses,
          item.disabled && disabledClasses
        );

        const content = (
          <>
            {item.icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{item.icon}</span>}
            <span>{item.label}</span>
          </>
        );

        if (item.href) {
          return (
            <NavLink
              key={item.key}
              to={item.href}
              aria-disabled={item.disabled}
              className={sharedClassName}
            >
              {content}
            </NavLink>
          );
        }

        const tabId = isTablist && tabPanelIdPrefix
          ? `${tabPanelIdPrefix}-tab-${item.key}`
          : undefined;
        const panelId = isTablist && tabPanelIdPrefix
          ? `${tabPanelIdPrefix}-panel-${item.key}`
          : undefined;

        return (
          <button
            key={item.key}
            ref={(el) => {
              if (el) tabRefs.current.set(item.key, el);
              else tabRefs.current.delete(item.key);
            }}
            type="button"
            id={tabId}
            role={isTablist ? 'tab' : undefined}
            aria-selected={isTablist ? active : undefined}
            aria-controls={isTablist ? panelId : undefined}
            // Only the active tab is in the natural tab order; arrow keys
            // navigate between tabs once focus enters the tablist.
            tabIndex={isTablist ? (active ? 0 : -1) : undefined}
            onClick={item.onSelect}
            onKeyDown={(e) => handleTabKeyDown(e, item.key)}
            disabled={item.disabled}
            className={sharedClassName}
          >
            {content}
          </button>
        );
      })}
    </nav>
  );
};

export default SegmentedNav;
